import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ServerCommunicationUtil } from '../utils/server_communication_util';

function useDropzoneUpload({
  url,
  paramName,
  formParam,
  acceptedFiles,
  maxFiles,
  maxFilesize,
  addedFileCallback = () => {},
  thumbnailCallback = () => {},
  removedFileCallback = () => {},
  successCallback = () => {},
  sendingCallback = () => {},
  progressCallback = () => {},
  queueCompleteCallback = () => {},
  handleErrorCallback = () => {},
  disabled
}) {
  const [dropzone, initDropzone] = useState(null);

  const removeDuplicateFiles = (file, files = [], removeFile) => {
    // we use files.length - 1 so it excludes the recently added file
    for (let i = 0; i < files.length - 1; i++) {
      if (files[i].name === file.name && files[i].size === file.size && files[i].lastModifiedDate.toString() === file.lastModifiedDate.toString()) {
        removeFile(file);
      }
    }
  };

  useEffect(() => {
    if (disabled) return;

    const dz = new Dropzone(formParam, {
      url,
      paramName,
      params: { key: '3p0t5s6b5g4g0e8k3c1j3w7y5c3m4t8i' },
      autoProcessQueue: false,
      acceptedFiles,
      clickable: true,
      parallelUploads: maxFiles,
      maxFiles,
      autoDiscover: false,
      maxFilesize,
      timeout: 60000,
      addRemoveLinks: true,
      previewsContainer: '#previewsContainer',
      headers: ServerCommunicationUtil.defaultAuthHeaders(),
      removedfile: () => {} // since we are not using the html given from the plugin, we dont want to try and remove any nodes that do not exist
    });
    dz.on('addedfile', function(file) {
      // this.removeFile is defined in the Dropzone plugin itself
      removeDuplicateFiles(file, this.files, this.removeFile);
      addedFileCallback(file);
    });

    dz.on('thumbnail', function(file, dataUrl) {
      thumbnailCallback(file, dataUrl);
    });
    dz.on('removedfile', function(file) {
      removedFileCallback(file);
    });
    dz.on('sending', function(file, xhr, formData) {
      sendingCallback(file, formData);
    });
    dz.on('success', function(file, response) {
      successCallback(file, response);
    });
    dz.on('totaluploadprogress', function(progress) {
      progressCallback(progress);
    });
    dz.on('queuecomplete', function() {
      let hasErrors = false;
      const failedUploads = this.files.filter(file => {
        return file.status === 'error';
      });

      if (failedUploads.length > 0) {
        hasErrors = true;
        failedUploads.forEach(upload => {
          upload.status = Dropzone.QUEUED;
        });
      }

      queueCompleteCallback(hasErrors);
    });
    dz.on('error', function(file, errorMessage, xhr) {
      if (errorMessage === "You can't upload files of this type.") {
        this.removeFile(file);
        return;
      }

      handleErrorCallback(file);
    });

    initDropzone(dz);
    return () => {
      dz.destroy();
      initDropzone(null);
    };
  }, []);

  return [dropzone, initDropzone];
}

useDropzoneUpload.PropTypes = {
  addedFileCallback: PropTypes.func,
  thumbnailCallback: PropTypes.func,
  removedFileCallback: PropTypes.func,
  sendingCallback: PropTypes.func,
  progressCallback: PropTypes.func,
  handleErrorCallback: PropTypes.func,
  maxFiles: PropTypes.number,
  maxFileSize: PropTypes.number
};

useDropzoneUpload.defaultProps = {
  addedFileCallback: () => {},
  thumbnailCallback: () => {},
  removedFileCallback: () => {},
  sendingCallback: () => {},
  successCallback: () => {},
  progressCallback: () => {},
  handleErrorCallback: () => {},
  maxFiles: 10,
  maxFilesize: 256
};

export default useDropzoneUpload;
